/**
 * useActiveLeagueTenant
 *
 * Whenever a route has a `:leagueId` URL param this hook ensures the active
 * tenant slug (used by the axios interceptor for path-prefix rewriting) is set
 * to the tenant that OWNS that league — not whatever the banner happened to
 * have selected.
 *
 * Algorithm:
 *  1. Accept the leagueId from the caller (via useParams in the page).
 *  2. Look it up in `uiSlice.bannerLeagues` (populated by the discovery fetch
 *     that fires on MainLayout mount).
 *  3. If found and the slug differs from the current selection, dispatch
 *     `setSelectedLeague` to update both Redux and the axios interceptor.
 *  4. Return `{ tenantSlug, resolved }`:
 *     - `tenantSlug` — the slug for this league (empty string while unresolved)
 *     - `resolved`   — true once the slug is known (guards data fetches)
 *
 * Resolution rules:
 *  - League found in bannerLeagues → resolved=true, tenant set.
 *  - bannerLoaded=true AND bannerLeagues is empty (discovery COMPLETED with no
 *    leagues, or is unavailable) → resolved=true with the current selectedTenantSlug.
 *    This handles test environments and gracefully degrades when discovery fails.
 *  - bannerLoaded=false (discovery not settled yet) → resolved=false, data fetches
 *    wait. This is the key guard: the initial pre-discovery state must NOT look the
 *    same as "discovery done & empty", or fetches leak to the registry on cold load.
 *
 * Precedence: URL `:leagueId` slug wins when present; banner-selected slug
 * otherwise.  Pages should gate their data fetches behind `resolved`.
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import { setSelectedLeague } from '../store/slices/uiSlice';

interface ActiveLeagueTenantResult {
  /** The resolved tenant slug for the league in the URL, or '' if still loading. */
  tenantSlug: string;
  /** True once we have confirmed the tenant slug for this leagueId. */
  resolved: boolean;
  /** Human-readable league name from the discovery data, or undefined while loading. */
  leagueName: string | undefined;
}

export function useActiveLeagueTenant(leagueId: string | undefined): ActiveLeagueTenantResult {
  const dispatch = useDispatch<AppDispatch>();
  const { bannerLeagues, selectedLeagueId, selectedTenantSlug, bannerLoaded } = useSelector(
    (state: RootState) => state.ui,
  );

  // Find the league entry from the discovery banner data
  const found = leagueId ? bannerLeagues.find(l => l.leagueId === leagueId) : undefined;

  useEffect(() => {
    if (!leagueId) return;
    if (!found) return; // still loading or unknown league — don't clobber current selection

    // Only update when necessary to avoid infinite re-renders
    if (selectedLeagueId !== leagueId || selectedTenantSlug !== found.tenantSlug) {
      dispatch(setSelectedLeague({ leagueId: found.leagueId, tenantSlug: found.tenantSlug }));
    }
  }, [dispatch, leagueId, found, selectedLeagueId, selectedTenantSlug]);

  if (!leagueId) {
    // No leagueId in URL — use whatever the banner has selected.
    // Treat as resolved only when we already have a slug, OR discovery has
    // actually COMPLETED and returned no leagues — never in the initial
    // pre-discovery state (which would leak a fetch to the registry).
    const resolvedWithBanner = !!selectedTenantSlug;
    const discoveryDoneEmpty = bannerLoaded && bannerLeagues.length === 0;
    return {
      tenantSlug: selectedTenantSlug ?? '',
      resolved: resolvedWithBanner || discoveryDoneEmpty,
      leagueName: undefined,
    };
  }

  if (!found) {
    if (!bannerLoaded) {
      // Discovery hasn't settled yet — wait before firing any fetch so
      // league-scoped pages don't leak to the registry on cold load.
      return { tenantSlug: '', resolved: false, leagueName: undefined };
    }
    // Discovery is done but this leagueId isn't in the results.
    // This happens in test environments (bannerLeagues: []) or when discovery
    // returns no leagues. Fall through with the currently-selected slug so pages
    // don't freeze forever.
    return {
      tenantSlug: selectedTenantSlug ?? '',
      resolved: true,
      leagueName: undefined,
    };
  }

  return {
    tenantSlug: found.tenantSlug,
    resolved: true,
    leagueName: found.name,
  };
}
